import RouteTranslationService from '../services/route-translation-service.js';
import {
	getDomain,
} from '../services/domain-service.js';

/**
 * A translator for routes. Needs a translation map from route-translation-service.js.
 */
export default class RouteTranslator {
	/**
	 * @returns {Promise<RouteTranslator>} The created translator.
	 */
	static createForCurrentDomain () {
		const domainName = getDomain();

		return new Promise((resolve) => {
			RouteTranslationService.getRouteTranslationMapForDomain(domainName)
				.then((routeTranslationMap) => {
					resolve(new RouteTranslator(routeTranslationMap, domainName));
				});
		});
	}

	/**
	 * @param {string} domainName The domain name for which the resulting class can translate routes.
	 * @param {boolean} allowCache
	 * @returns {Promise<RouteTranslator>} The created translator.
	 */
	static createForDomain (domainName, allowCache = true) {
		return RouteTranslationService.getRouteTranslationMapForDomain(domainName, allowCache)
			.then((routeTranslationMap) => {
				return (new RouteTranslator(routeTranslationMap, domainName));
			});
	}

	/**
	 * @param {object} routeTranslationMap An object with route segments as keys and translated segments as values.
	 * @param {string} domainName The name of the domain for which this class can translate.
	 */
	constructor (routeTranslationMap, domainName) {
		this.domainName = domainName;
		this.segmentMap = routeTranslationMap;
	}

	/**
	 * Translate the route, only the path, segment by segment
	 *
	 * @param {string} route The route to translate.
	 * @returns {string} The translated route.
	 */
	translateRoute (route) {
		const [path, query, ] = this.deconstructRoute(route);
		const translatedPath = this.translatePath(path);

		return this.reconstructRoute(translatedPath, query);
	}

	/**
	 * @param {string[]} routes The routes to translate.
	 * @returns {object[]} The routes with their translations.
	 */
	translateRoutes (routes) {
		const routesWithTranslations = [];

		for (const route of routes) {
			routesWithTranslations.push({
				route: route, translation: this.translateRoute(route)
			});
		}
		return routesWithTranslations;
	}

	/**
	 * Translate a path, segment by segment
	 *
	 * @param {string} path The path to translate.
	 * @returns {string} The translated path.
	 */
	translatePath (path) {
		return path
			.split('/')
			.map(this.translateSegment.bind(this))
			.join('/');
	}

	/**
	 * Translates a given segment, if a translation is available
	 *
	 * @param {string} segment The segment to translate
	 * @returns {string} The (possibly) translated segment.
	 */
	translateSegment (segment) {
		return this.translationExists(segment) ? this.segmentMap[segment] : segment;
	}

	/**
	 * Check if there is a translation for the given path segments
	 *
	 * @param {string} segment The segment to find a translation for.
	 * @returns {boolean} Whether there is a translation for that segment or not.
	 */
	translationExists (segment) {
		return Object.keys(this.segmentMap).includes(segment);
	}

	/**
	 * Deconstruct a route into path and query
	 *
	 * @param {string} route The route to deconstruct.
	 * @returns {string[]} An array containing the path and the query (query is "" if there is no query).
	 */
	deconstructRoute (route) {
		const [, path, query, ] = /([^?]*)[?]?(.*)/.exec(route);

		return [path, query, ];
	}

	/**
	 * Reconstruct a route from path and query (query is optional)
	 *
	 * @param {string} path The path for the route.
	 * @param {string} query The query for the route.
	 * @returns {string} The reconstructed route.
	 */
	reconstructRoute (path, query) {
		return `${path}${query ? `?${query}` : ''}`;
	}

	/**
	 * @param {string} domain
	 * @returns {boolean}
	 */
	isForDomain (domain) {
		return this.domainName.toLowerCase() === domain.toLowerCase();
	}
}
